import React, { useContext, useEffect } from 'react';
import { useDeviceSelectors } from 'react-device-detect';
import { isAppInstalled, POPUP_TUTO_STATE } from 'helpers/utils';
import { hasInstallPrompt, onInstallPromptReady, promptInstall } from 'helpers/installPrompt';
import { LS_KEYS } from 'constants/global';
import { ModalContext, ModalContextProps } from 'contexts/ModalContext';
import InstallationTutoModal, {
  InstallationTutoModalProps,
} from 'components/modal/installation/InstallationTutoModal';
import { useTranslation } from 'react-i18next';
import IconShare from 'assets/svg/icon-share.svg';

// Since iOS 16.4, Chrome / Firefox / Edge can add a web app to the Home Screen
// (before that only Safari could) and web push is available.
const MIN_IOS_VERSION = 16.4;

// Chromium fires beforeinstallprompt shortly after load once the install
// criteria are met (service worker active, manifest...). Past this delay we
// assume it will not come (recently dismissed, first visit) and show the menu
// steps instead.
const INSTALL_PROMPT_WAIT_MS = 4000;

// The Chromium install invite is not repeated on every load (unlike the
// iOS / Firefox tutorial, which has no native prompt to rely on)
const CHROMIUM_TUTO_SNOOZE_MS = 24 * 60 * 60 * 1000;

const parseMajorMinor = (version?: string): number => {
  if (!version) return 0;
  const [major, minor] = version.split('.');
  return parseFloat(`${major}.${minor || 0}`) || 0;
};

// Brave hides behind Chrome's (Android) or Safari's (iOS) user agent, so
// navigator.brave.isBrave() is the only way to recognise it.
const detectBrave = async (): Promise<boolean> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const brave = (window.navigator as any).brave;
    return !!brave && typeof brave.isBrave === 'function' && (await brave.isBrave()) === true;
  } catch {
    return false;
  }
};

const isChromiumTutoSnoozed = (): boolean => {
  try {
    const shownAt = Number(window.localStorage.getItem(LS_KEYS.INSTALL_PROMPT_SHOWN_AT) || 0);
    return Date.now() - shownAt < CHROMIUM_TUTO_SNOOZE_MS;
  } catch {
    return false;
  }
};

const markChromiumTutoShown = (): void => {
  try {
    window.localStorage.setItem(LS_KEYS.INSTALL_PROMPT_SHOWN_AT, String(Date.now()));
  } catch {
    // storage unavailable: the invite simply shows again next time
  }
};

function useCheckInstallation() {
  const { t } = useTranslation('translation');
  const { updateModalConf } = useContext(ModalContext) as ModalContextProps;
  const [selectors, data] = useDeviceSelectors(window.navigator.userAgent);

  const { isIOS, isAndroid, isMobile } = selectors;
  // iPadOS advertises itself as a Mac: only the touch screen gives it away
  const isIPadOS =
    /Macintosh/i.test(window.navigator.userAgent) && window.navigator.maxTouchPoints > 1;
  // Chromium browsers: they own a native install dialog (beforeinstallprompt)
  // eslint-disable-next-line no-prototype-builtins
  const canInstallAuto = window.hasOwnProperty('BeforeInstallPromptEvent');
  const shareIcon = `<img class="icon-share" src="${IconShare}" />`;

  const showModal = (modalProps: InstallationTutoModalProps) => {
    updateModalConf({
      component: <InstallationTutoModal key={Math.random()} {...modalProps} />,
    });
    POPUP_TUTO_STATE.setState(true);
  };

  // iOS: the share sheet offers "Add to Home Screen" in Safari, Chrome and Edge
  // (share icon in the address bar) and in Firefox (behind its "..." menu).
  // Brave never added it on iOS: the user has to go through Safari.
  const buildIOSProps = (isBrave: boolean): InstallationTutoModalProps => {
    const modalProps: InstallationTutoModalProps = {
      title:
        t('settings.installation.plateform_ios.title', {
          iOSDevice: data.device?.model || (isIPadOS ? 'iPad' : ''),
        }) || '',
    };

    if (isBrave) {
      modalProps.message =
        t('settings.installation.plateform_ios.brave_note', { iconShare: shareIcon }) || '';
      return modalProps;
    }

    const isFirefox = /firefox/i.test(data.browser?.name || '');
    modalProps.steps = [
      isFirefox
        ? t('settings.installation.plateform_ios.steps.step0_firefox') || ''
        : t('settings.installation.plateform_ios.steps.step0', { iconShare: shareIcon }) || '',
      t('settings.installation.plateform_ios.steps.step1') || '',
    ];

    // Brave detection can be blocked by the browser itself: always tell iOS
    // users how to fall back on Safari when the option is missing.
    const notes = [t('settings.installation.plateform_ios.safari_fallback_note') || ''];
    // os.version is the iOS version; browser.version would be Chrome's / Firefox's own
    const iOSVersion = parseMajorMinor(data.os?.version || data.browser?.version);
    if (iOSVersion && iOSVersion < MIN_IOS_VERSION) {
      notes.unshift(t('settings.installation.plateform_ios.additionnal_note') || '');
    }
    modalProps.message = notes.filter(Boolean).join('<br /><br />');

    return modalProps;
  };

  // Android browsers without the native dialog (Firefox, or Chromium once the
  // prompt was declined) still install from their menu: "Install app" or
  // "Add to Home screen".
  const buildAndroidProps = (): InstallationTutoModalProps => ({
    title: t('settings.installation.plateform_android.title') || '',
    steps: [
      t('settings.installation.plateform_android.steps.step0') || '',
      t('settings.installation.plateform_android.steps.step1') || '',
    ],
  });

  // Chromium fallback when the native dialog is unavailable or was declined
  const buildChromiumFallbackProps = (): InstallationTutoModalProps =>
    isMobile && isAndroid
      ? buildAndroidProps()
      : { message: t('settings.installation.plateform_chromium.fallback_desktop') || '' };

  // Chromium: an in-app button opens the browser's own install dialog
  const openChromiumTuto = () => {
    markChromiumTutoShown();

    const install = async () => {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        updateModalConf(); // close: the browser takes over the installation
        return;
      }
      // Declined, or the event is gone: explain the browser menu path instead
      updateModalConf({
        component: <InstallationTutoModal key={Math.random()} {...buildChromiumFallbackProps()} />,
      });
    };

    showModal({
      title: t('settings.installation.plateform_chromium.title') || '',
      message: t('settings.installation.plateform_chromium.message') || '',
      action: {
        label: t('settings.installation.plateform_chromium.cta') || '',
        onClick: install,
      },
    });
  };

  // Browsers without a native install dialog: step-by-step tutorial
  const openPopupTuto = async () => {
    const showedFirefoxTuto = typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem('showedFirefoxTuto')
      : null;
    let modalProps: InstallationTutoModalProps = {};

    try {
      console.log('UA info: ', data);
      if ((isMobile && isIOS) || isIPadOS) {
        modalProps = buildIOSProps(await detectBrave());
      } else if (isMobile && isAndroid) {
        modalProps = buildAndroidProps();
      } else if (!showedFirefoxTuto) {
        // Desktop browser without install support (Firefox...): shown once
        modalProps = {
          message: t('settings.installation.plateform_others') || '',
        };
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('showedFirefoxTuto', 'true');
        }
      }
    } catch (e) {
      console.log('error building installation tuto: ', e);
    }

    if (modalProps.title || modalProps.message || modalProps.steps) {
      showModal(modalProps);
    }
  };

  // Chromium: wait for beforeinstallprompt, then offer the native dialog.
  // Returns the cleanup for the effect.
  const initChromiumCheck = (): (() => void) | undefined => {
    if (isChromiumTutoSnoozed()) return undefined;

    if (hasInstallPrompt()) {
      openChromiumTuto();
      return undefined;
    }

    // No prompt in time (first visit, recently declined...): menu steps on Android,
    // nothing on desktop where the address bar already offers the install icon
    const timer = setTimeout(() => {
      unsubscribe();
      if (isMobile && isAndroid && !POPUP_TUTO_STATE.getState()) {
        markChromiumTutoShown();
        showModal(buildAndroidProps());
      }
    }, INSTALL_PROMPT_WAIT_MS);
    const unsubscribe = onInstallPromptReady(() => {
      clearTimeout(timer);
      unsubscribe();
      if (!POPUP_TUTO_STATE.getState()) {
        openChromiumTuto();
      }
    });

    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  };

  const initCheck = (): (() => void) | undefined => {
    // Already installed (standalone) or tutorial already shown this session
    if (isAppInstalled() || POPUP_TUTO_STATE.getState()) return undefined;

    if (canInstallAuto) {
      return initChromiumCheck();
    }

    openPopupTuto();
    return undefined;
  };

  useEffect(() => {
    return initCheck();
  }, []);
}

export default useCheckInstallation;
